import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { trackRedirect } from '@/lib/auth-debug';

type RedirectRouter = Pick<AppRouterInstance, 'replace'>;

const lastRedirect = {
  destination: '',
  at: 0,
};

/**
 * Mandatory onboarding gates live under `/dashboard/admin/*`. Without this list,
 * `shouldSkipRedirect('/dashboard/admin/setup-2fa', '/dashboard/admin')` would
 * treat the gate as "already under destination" and never navigate — leaving the
 * admin layout stuck on "Redirecting...".
 */
const ADMIN_ONBOARDING_GATE_PATHS = [
  '/dashboard/admin/setup-2fa',
  '/dashboard/admin/change-password',
  '/admin/setup-2fa',
  '/admin/change-password',
] as const;

function normalizePath(path: string): string {
  return path.split('?')[0].replace(/\/$/, '') || '/';
}

function isAdminOnboardingGatePath(pathname: string): boolean {
  return ADMIN_ONBOARDING_GATE_PATHS.some(
    (gate) => pathname === gate || pathname.startsWith(`${gate}/`)
  );
}

export function shouldSkipRedirect(pathname: string | null | undefined, destination: string): boolean {
  if (!pathname || !destination) return true;

  const current = normalizePath(pathname);
  const target = normalizePath(destination);

  if (current === target) return true;

  // Nested under destination is usually "already there" (e.g. /dashboard/admin/users
  // while targeting /dashboard/admin). Exception: leave 2FA / password-change gates.
  if (current.startsWith(`${target}/`) && !isAdminOnboardingGatePath(current)) {
    return true;
  }

  const now = Date.now();
  if (lastRedirect.destination === target && now - lastRedirect.at < 2000) {
    return true;
  }

  return false;
}

/** Client-side redirect with loop and duplicate protection. */
export function safeReplace(
  router: RedirectRouter,
  pathname: string | null | undefined,
  destination: string
): boolean {
  if (shouldSkipRedirect(pathname, destination)) {
    return false;
  }

  trackRedirect(destination);
  lastRedirect.destination = normalizePath(destination);
  lastRedirect.at = Date.now();
  router.replace(destination);
  return true;
}

export function resetRedirectGuard(): void {
  lastRedirect.destination = '';
  lastRedirect.at = 0;
}
